import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Card, Form, Result } from "antd";
import styles from "../Auth/Auth.module.css";
import Loading from "../../fragments/Loading";
import NewPasswordFields from "../../fragments/NewPasswordFields";
import { acceptInvitation, lookupInvitation } from "../../utils";
import { tokenFromHash } from "../../utils/links";
import { useSession } from "../../session/SessionContext";

interface Invitation {
  email: string;
  organization: { id: string; name: string };
}

const AcceptInvitation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn } = useSession();
  const token = tokenFromHash(location.hash);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    lookupInvitation(token)
      .then(setInvitation)
      .catch((error: Error) => setLoadError(error.message));
  }, [token]);

  const handleSubmit = async ({ password }: { password: string }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { token: sessionToken } = await acceptInvitation(token, password);
      await signIn(sessionToken);
      navigate("/", { replace: true });
    } catch (error) {
      setSubmitError((error as Error).message);
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <Result
        status="warning"
        title="This invitation cannot be used"
        subTitle={`${loadError} Ask the person who invited you for a new link.`}
        extra={<Button onClick={() => navigate("/login")}>Go to sign in</Button>}
      />
    );
  }
  if (!invitation) {
    return <Loading />;
  }

  return (
    <div className={styles.authWrapper}>
      <div className={styles.authCard}>
        <div className={styles.authLogo}>Brixture</div>
        <Card title={`Join ${invitation.organization.name}`}>
          <p>
            You are signing up as <strong>{invitation.email}</strong>. Choose a password to
            finish.
          </p>
          <Form layout="vertical" onFinish={handleSubmit}>
            <NewPasswordFields />
            {submitError && <Alert type="error" title={submitError} style={{ marginBottom: 16 }} />}
            <Button type="primary" htmlType="submit" loading={submitting}>
              Create account
            </Button>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvitation;
